import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Edit2, Eye, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { customersAPI } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);

  useEffect(() => {
    loadCustomers();
  }, [activeOnly, searchTerm]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm || undefined,
        active_only: activeOnly,
        limit: 100
      };
      const response = await customersAPI.getAll(params);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Möchten Sie den Kunden "${customer.name}" wirklich löschen?`)) {
      return;
    }

    try {
      await customersAPI.delete(customer.id);
      toast.success('Kunde gelöscht');
      loadCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Fehler beim Löschen des Kunden');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div>
      <div className="content-header">
        <div>
          <h1>Kunden</h1>
          <p>Verwalten Sie Ihre Kundendaten</p>
        </div>
        <Link to="/customers/new" className="btn btn-primary">
          <Plus size={16} />
          Neuer Kunde
        </Link>
      </div>

      <div className="content-body">
        {/* Filters */}
        <div className="card mb-6">
          <div className="card-body">
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Kunden suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="activeOnly"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                  className="form-checkbox"
                />
                <label htmlFor="activeOnly" className="text-sm font-medium">
                  Nur aktive Kunden
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Table */}
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="large" />
          </div>
        ) : (
          <div className="table-container">
            {customers.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th>Ort</th>
                    <th>Monatliche Gebühr</th>
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id}>
                      <td className="font-semibold">{customer.name}</td>
                      <td>{customer.email || '-'}</td>
                      <td>{customer.city}</td>
                      <td>{formatCurrency(customer.monthly_fee || 0)}</td>
                      <td>
                        <span className={customer.active ? 'status-badge status-active' : 'status-badge status-inactive'}>
                          {customer.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <Link
                            to={`/customers/${customer.id}`}
                            className="btn btn-outline btn-sm"
                            title="Details anzeigen"
                          >
                            <Eye size={14} />
                          </Link>
                          <Link
                            to={`/customers/${customer.id}/edit`}
                            className="btn btn-outline btn-sm"
                            title="Bearbeiten"
                          >
                            <Edit2 size={14} />
                          </Link>
                          <button
                            onClick={() => handleDelete(customer)}
                            className="btn btn-danger btn-sm"
                            title="Löschen"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <Users size={48} className="mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">Keine Kunden gefunden</h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm
                    ? 'Keine Kunden entsprechen Ihren Suchkriterien.'
                    : 'Sie haben noch keine Kunden angelegt.'}
                </p>
                <Link to="/customers/new" className="btn btn-primary">
                  <Plus size={16} />
                  Ersten Kunden anlegen
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;